/**
 * The video hosts an execution step is allowed to point at (T-805).
 *
 * A step stores a single URL, typed by hand. Only a link we can turn into an
 * embed is any use to a cook, so the same function that builds the embed also
 * answers whether the URL is acceptable — the input can then refuse a bad one
 * on the spot instead of saving a link that quietly renders nothing.
 */

export type VideoProvider = "youtube" | "vimeo";

export interface StepVideo {
  provider: VideoProvider;
  id: string;
  embedUrl: string;
}

const YOUTUBE_ID_PATTERN = /^[A-Za-z0-9_-]{11}$/;
const VIMEO_ID_PATTERN = /^\d+$/;
const VIMEO_HASH_PATTERN = /^[A-Za-z0-9]+$/;

const YOUTUBE_HOSTS = new Set([
  "youtube.com",
  "www.youtube.com",
  "m.youtube.com",
  "youtu.be",
  "www.youtu.be",
]);

const VIMEO_HOSTS = new Set(["vimeo.com", "www.vimeo.com", "player.vimeo.com"]);

function isYouTubeId(id: string | null | undefined): id is string {
  return !!id && YOUTUBE_ID_PATTERN.test(id);
}

function parseYouTube(parsed: URL): StepVideo | null {
  const host = parsed.hostname.toLowerCase();
  const segments = parsed.pathname.split("/").filter(Boolean);

  let id: string | null = null;

  if (host === "youtu.be" || host === "www.youtu.be") {
    id = segments[0] ?? null;
  } else if (parsed.pathname === "/watch") {
    id = parsed.searchParams.get("v");
  } else if (["shorts", "embed", "live", "v"].includes(segments[0] ?? "")) {
    id = segments[1] ?? null;
  }

  if (!isYouTubeId(id)) return null;

  return { provider: "youtube", id, embedUrl: `https://www.youtube.com/embed/${id}` };
}

function parseVimeo(parsed: URL): StepVideo | null {
  const segments = parsed.pathname.split("/").filter(Boolean);

  // Vimeo puts the numeric id in the last path position that matters, but the
  // path in front of it varies: /<id>, /video/<id>, /channels/<name>/<id>,
  // /groups/<name>/videos/<id>. The first all-digit segment is the id in every
  // one of those; a segment straight after it is the unlisted-link hash, which
  // newer links carry as ?h= instead.
  const idIndex = segments.findIndex((segment) => VIMEO_ID_PATTERN.test(segment));
  const id = idIndex === -1 ? null : segments[idIndex]!;
  if (!id) return null;

  const trailing = segments[idIndex + 1];
  const hash =
    parsed.searchParams.get("h") ??
    (trailing && VIMEO_HASH_PATTERN.test(trailing) ? trailing : null);

  const embedUrl = hash
    ? `https://player.vimeo.com/video/${id}?h=${hash}`
    : `https://player.vimeo.com/video/${id}`;

  return { provider: "vimeo", id, embedUrl };
}

/** The video a step points at, or null when the URL is missing or unusable. */
export function parseStepVideo(rawUrl?: string | null): StepVideo | null {
  const value = rawUrl?.trim();
  if (!value) return null;

  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return null;
  }

  const host = parsed.hostname.toLowerCase();
  if (YOUTUBE_HOSTS.has(host)) return parseYouTube(parsed);
  if (VIMEO_HOSTS.has(host)) return parseVimeo(parsed);
  return null;
}

export function isValidVideoUrl(rawUrl?: string | null): boolean {
  return parseStepVideo(rawUrl) !== null;
}

export function toVideoEmbedUrl(rawUrl?: string | null): string | null {
  return parseStepVideo(rawUrl)?.embedUrl ?? null;
}
